/**
 * Data Tab Prompt Builder
 *
 * Generates an Entity-Relationship diagram from data objects,
 * showing entities with their attributes, relationships, and classifications.
 */

import type { BulkEAImport } from '../../canister-client';
import type { ChatMessage } from '../../canister-client';
import { baseSvgRules, summarizeEntities } from './base-svg-prompt';

export function buildDataPrompt(data: BulkEAImport): ChatMessage[] {
    const dataObjs = summarizeEntities(data.data_objects, [
        'name', 'domain', 'source_system', 'owner', 'classification',
        'retention', 'key_attributes',
    ]);
    const dataRels = data.relationships
        .filter(r => r.source_type === 'DataObject' || r.target_type === 'DataObject')
        .slice(0, 30);
    const relsSummary = summarizeEntities(dataRels, [
        'source_id', 'relationship_type', 'target_id',
    ]);

    return [
        {
            role: 'user',
            content: `${baseSvgRules()}

DIAGRAM TYPE: Entity-Relationship Diagram (ERD)

DATA - Data Objects:
${dataObjs}

DATA - Data Relationships:
${relsSummary || '(derive from shared domains and source systems)'}

INSTRUCTIONS:
- Create an ER diagram with data objects as entity boxes.
- Each entity box has three sections: name (header), key attributes, metadata.
- Key attributes are listed from the "key_attributes" field.
- Metadata section shows: classification, retention, source system.
- Color-code classification with a badge: Confidential=red, Internal=amber, Public=green.
- Draw relationship lines between entities with cardinality labels (1:N, N:M).
- Group entities by domain into labeled regions.
- Add a title "Data Architecture - Entity Relationships" at the top.
- Include a classification legend.`,
        },
    ];
}
